import { useEffect, useState, type FormEvent } from 'react';
import { Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { ExternalLink, Pencil, Plus, Server, Trash2 } from 'lucide-react';
import {
  createLicense, deleteLicense, findLicenseIdByDeviceId, listLicenses, updateLicense,
  type LicenseInput,
} from '@/lib/firestore';
import { licenseStatus } from '@/lib/licenseStatus';
import { monitoringUrl } from '@/lib/monitoring';
import { useT, type Locale } from '@/lib/i18n';
import type { LicenseDoc, Organization } from '@/types/models';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader } from '@/components/ui/loader';
import {
  Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell,
} from '@/components/ui/table';

interface FormState {
  deviceId: string;
  deviceType: string;
  orgId: string;
  releaseVersion: string;
  firstDeployment: string;   // yyyy-mm-dd (input type="date")
  licenceDeployed: string;
  licenceExpires: string;
}

const emptyForm: FormState = {
  deviceId: '', deviceType: '', orgId: '', releaseVersion: '',
  firstDeployment: '', licenceDeployed: '', licenceExpires: '',
};

function toInputDate(ts?: Timestamp | null): string {
  if (!ts) return '';
  const d = ts.toDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fromInputDate(s: string): Timestamp | null {
  return s ? Timestamp.fromDate(new Date(`${s}T00:00:00`)) : null;
}

function fmtDay(ts: Timestamp | null | undefined, locale: Locale): string {
  if (!ts) return '—';
  return ts.toDate().toLocaleDateString(locale === 'en' ? 'en-CA' : 'fr-CA', { dateStyle: 'medium' });
}

function toForm(l: LicenseDoc): FormState {
  return {
    deviceId: l.deviceId,
    deviceType: l.deviceType,
    orgId: l.orgId,
    releaseVersion: l.releaseVersion ?? '',
    firstDeployment: toInputDate(l.firstDeployment),
    licenceDeployed: toInputDate(l.licenceDeployed),
    licenceExpires: toInputDate(l.licenceExpires),
  };
}

export function LicenseTable({ orgs }: { orgs: Organization[] }) {
  const { t, locale } = useT();
  const [licenses, setLicenses] = useState<LicenseDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LicenseDoc | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { setLicenses(await listLicenses()); }
    catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (l: LicenseDoc) => {
    setEditing(l);
    setForm(toForm(l));
    setOpen(true);
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  const onSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const deviceId = form.deviceId.trim();
    const deviceType = form.deviceType.trim();
    const org = orgs.find(o => o.id === form.orgId);
    if (!deviceId || !deviceType || !org) { toast.error(t('admin.licenses.required')); return; }

    setSaving(true);
    try {
      const existingId = await findLicenseIdByDeviceId(deviceId);
      if (existingId && existingId !== editing?.id) { toast.error(t('admin.licenses.duplicate')); return; }

      const input: LicenseInput = {
        deviceId,
        deviceType,
        orgId: org.id,
        orgName: org.displayName,
        releaseVersion: form.releaseVersion.trim() || null,
        firstDeployment: fromInputDate(form.firstDeployment),
        licenceDeployed: fromInputDate(form.licenceDeployed),
        licenceExpires: fromInputDate(form.licenceExpires),
      };
      if (editing) await updateLicense(editing.id, input);
      else await createLicense(input);

      toast.success(t('admin.licenses.saved'));
      setOpen(false);
      await refresh();
    } catch (err) { toast.error((err as Error).message); }
    finally { setSaving(false); }
  };

  const onDelete = async (l: LicenseDoc) => {
    if (!confirm(t('admin.licenses.delete_confirm'))) return;
    try {
      await deleteLicense(l.id);
      toast.success(t('admin.licenses.deleted'));
      await refresh();
    } catch (err) { toast.error((err as Error).message); }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg text-ink-900 dark:text-white">
          <Server className="h-5 w-5 text-brand-500" />
          {t('admin.licenses.title')}
        </h2>
        <Button onClick={openNew}><Plus className="h-4 w-4" /> {t('admin.licenses.new')}</Button>
      </div>

      {loading ? <Loader /> : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell>{t('admin.licenses.col.device_id')}</TableHeadCell>
              <TableHeadCell>{t('admin.licenses.col.device_type')}</TableHeadCell>
              <TableHeadCell>{t('admin.licenses.col.org')}</TableHeadCell>
              <TableHeadCell>{t('admin.licenses.col.release_version')}</TableHeadCell>
              <TableHeadCell>{t('admin.licenses.col.first_deployment')}</TableHeadCell>
              <TableHeadCell>{t('admin.licenses.col.licence_deployed')}</TableHeadCell>
              <TableHeadCell>{t('admin.licenses.col.licence_expires')}</TableHeadCell>
              <TableHeadCell>{t('admin.licenses.col.status')}</TableHeadCell>
              <TableHeadCell>{t('admin.licenses.col.health')}</TableHeadCell>
              <TableHeadCell><span className="sr-only">{t('admin.licenses.col.actions')}</span></TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {licenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-ink-500">{t('admin.licenses.empty')}</TableCell>
              </TableRow>
            )}
            {licenses.map(l => {
              const status = licenseStatus(l.licenceExpires?.toDate() ?? null);
              return (
              <TableRow key={l.id}>
                <TableCell className="font-mono text-xs">{l.deviceId}</TableCell>
                <TableCell>{l.deviceType}</TableCell>
                <TableCell>{l.orgName}</TableCell>
                <TableCell>{l.releaseVersion || '—'}</TableCell>
                <TableCell>{fmtDay(l.firstDeployment, locale)}</TableCell>
                <TableCell>{fmtDay(l.licenceDeployed, locale)}</TableCell>
                <TableCell>{fmtDay(l.licenceExpires, locale)}</TableCell>
                <TableCell>
                  <Badge variant={status === 'active' ? 'success' : 'warning'}>
                    {t(status === 'active' ? 'admin.licenses.status.active' : 'admin.licenses.status.expired')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <a
                    href={monitoringUrl(l.deviceId, l.orgId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-brand-600 hover:underline dark:text-brand-300"
                  >
                    {t('admin.licenses.health_link')} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" aria-label={t('admin.licenses.edit')} onClick={() => openEdit(l)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => onDelete(l)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? t('admin.licenses.modal.edit') : t('admin.licenses.modal.new')}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button type="submit" form="license-form" disabled={saving}>{t('common.save')}</Button>
          </>
        }
      >
        <form id="license-form" onSubmit={onSave} className="grid gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>{t('admin.licenses.field.device_id')}</Label>
              <Input required className="mt-1" value={form.deviceId} onChange={e => set('deviceId', e.target.value)} />
            </div>
            <div>
              <Label>{t('admin.licenses.field.device_type')}</Label>
              <Input required className="mt-1" value={form.deviceType} onChange={e => set('deviceType', e.target.value)} />
            </div>
          </div>
          <div>
            <Label>{t('admin.licenses.field.org')}</Label>
            <select
              required
              className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm dark:border-border dark:bg-muted dark:text-white"
              value={form.orgId}
              onChange={e => set('orgId', e.target.value)}
            >
              <option value="">{t('admin.licenses.field.org_placeholder')}</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.displayName}</option>)}
            </select>
          </div>
          <div>
            <Label>{t('admin.licenses.field.release_version')}</Label>
            <Input className="mt-1" value={form.releaseVersion} onChange={e => set('releaseVersion', e.target.value)} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label>{t('admin.licenses.field.first_deployment')}</Label>
              <Input type="date" className="mt-1" value={form.firstDeployment} onChange={e => set('firstDeployment', e.target.value)} />
            </div>
            <div>
              <Label>{t('admin.licenses.field.licence_deployed')}</Label>
              <Input type="date" className="mt-1" value={form.licenceDeployed} onChange={e => set('licenceDeployed', e.target.value)} />
            </div>
            <div>
              <Label>{t('admin.licenses.field.licence_expires')}</Label>
              <Input type="date" className="mt-1" value={form.licenceExpires} onChange={e => set('licenceExpires', e.target.value)} />
            </div>
          </div>
        </form>
      </Dialog>
    </section>
  );
}
